import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAgents } from "@/api/agents";
import { useUsageSummary } from "@/api/usage";
import type { UsageByJob } from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";
import { RelativeTime } from "@/components/RelativeTime";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  compact,
  diffMsFromIso,
  formatDurationMs,
  formatJobNumber,
  ms,
  usd,
} from "@/lib/format";
import { cn } from "@/lib/utils";

const SINCE_OPTIONS: Array<{ label: string; hours: number }> = [
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
];

/**
 * Columns the runs table can sort by. The default `cost desc` keeps the
 * "where did the money go" framing even after the table grew timestamp +
 * latency columns. Click a header to toggle direction; click a different
 * header to switch column (resets to `desc` for the new column).
 */
type RunsSortKey = "cost" | "started" | "duration" | "calls" | "avgLatency";
interface RunsSortState {
  key: RunsSortKey;
  dir: "asc" | "desc";
}
const DEFAULT_RUNS_SORT: RunsSortState = { key: "cost", dir: "desc" };

export function CostDashboard() {
  const [params, setParams] = useSearchParams();
  const sinceParam = Number(params.get("since")) || 24;
  const sinceHours = SINCE_OPTIONS.find((o) => o.hours === sinceParam)?.hours ?? 24;

  const query = useUsageSummary({ sinceHours });
  const { agentsByType } = useAgents();
  const [runsSort, setRunsSort] = useState<RunsSortState>(DEFAULT_RUNS_SORT);

  const sortedRuns = useMemo(
    () => sortRuns(query.data?.byJobTopN ?? [], runsSort),
    [query.data?.byJobTopN, runsSort],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="label-mono">Cost</p>
          <h1 className="font-display text-4xl text-text">Usage & spend</h1>
        </div>
        <Select
          value={String(sinceHours)}
          onValueChange={(value) => setParams({ since: value })}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SINCE_OPTIONS.map((o) => (
              <SelectItem key={o.hours} value={String(o.hours)}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {query.isPending ? (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : query.isError ? (
        <ErrorBanner error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Totals</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Stat label="Calls" value={compact(query.data.totals.callCount)} />
              <Stat label="Total cost" value={usd(query.data.totals.costUsd)} />
              <Stat label="Avg latency" value={ms(query.data.totals.avgLatencyMs)} />
              <Stat label="Input tokens" value={compact(query.data.totals.inputTokens)} />
              <Stat label="Output tokens" value={compact(query.data.totals.outputTokens)} />
              <Stat label="Cache writes" value={compact(query.data.totals.cacheCreationInputTokens)} />
              <Stat label="Cache reads" value={compact(query.data.totals.cacheReadInputTokens)} />
            </CardContent>
          </Card>

          <section className="space-y-3">
            <h2 className="label-mono">By agent</h2>
            <div className="border border-border rounded bg-bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead>Calls</TableHead>
                    <TableHead>Input</TableHead>
                    <TableHead>Output</TableHead>
                    <TableHead>Cache reads</TableHead>
                    <TableHead>Cache savings</TableHead>
                    <TableHead>Avg latency</TableHead>
                    <TableHead>Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {query.data.byAgent.map((row) => (
                    <TableRow key={row.agentType}>
                      <TableCell className="text-sm" title={row.agentType}>
                        {agentsByType[row.agentType]?.name ?? row.agentType}
                      </TableCell>
                      <TableCell>{compact(row.callCount)}</TableCell>
                      <TableCell>{compact(row.inputTokens)}</TableCell>
                      <TableCell>{compact(row.outputTokens)}</TableCell>
                      <TableCell>{compact(row.cacheReadInputTokens)}</TableCell>
                      <TableCell className="text-emerald-300">{usd(row.cacheSavingsUsd)}</TableCell>
                      <TableCell>{ms(row.avgLatencyMs)}</TableCell>
                      <TableCell>{usd(row.costUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="label-mono">Top jobs (by cost)</h2>
            <div className="border border-border rounded bg-bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job</TableHead>
                    <TableHead>Agent</TableHead>
                    <SortableHead
                      label="Started"
                      sortKey="started"
                      sort={runsSort}
                      onChange={setRunsSort}
                    />
                    <SortableHead
                      label="Duration"
                      sortKey="duration"
                      sort={runsSort}
                      onChange={setRunsSort}
                    />
                    <SortableHead
                      label="Calls"
                      sortKey="calls"
                      sort={runsSort}
                      onChange={setRunsSort}
                    />
                    <SortableHead
                      label="Avg latency"
                      sortKey="avgLatency"
                      sort={runsSort}
                      onChange={setRunsSort}
                    />
                    <SortableHead
                      label="Cost"
                      sortKey="cost"
                      sort={runsSort}
                      onChange={setRunsSort}
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRuns.map((row) => (
                    // The orchestrator groups `byJobTopN` by
                    // `(job_id, agent_type)`, so a paired-agent run
                    // (curator + validator on the same job_id) emits
                    // two rows with the same `jobId`. Include
                    // `agentType` in the React key to keep the
                    // reconciler honest.
                    <TableRow key={`${row.jobId}:${row.agentType}`}>
                      <TableCell>
                        <Link
                          to={`/jobs/${row.jobNumber !== null ? formatJobNumber(row.jobNumber) : row.jobId}`}
                          className="font-mono text-xs text-accent hover:underline"
                          title={row.jobId}
                        >
                          {row.jobNumber !== null
                            ? `#${formatJobNumber(row.jobNumber)}`
                            : `${row.jobId.slice(0, 12)}…`}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm" title={row.agentType}>
                        {agentsByType[row.agentType]?.name ?? row.agentType}
                      </TableCell>
                      <TableCell className="text-xs">
                        {row.startedAt ? <RelativeTime date={row.startedAt} /> : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{formatDuration(row)}</TableCell>
                      <TableCell>{compact(row.callCount)}</TableCell>
                      <TableCell>{ms(row.avgLatencyMs)}</TableCell>
                      <TableCell>{usd(row.costUsd)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="label-mono">{label}</p>
      <p className="font-display text-2xl text-text">{value}</p>
    </div>
  );
}

interface SortableHeadProps {
  label: string;
  sortKey: RunsSortKey;
  sort: RunsSortState;
  onChange: (next: RunsSortState) => void;
}

function SortableHead({ label, sortKey, sort, onChange }: SortableHeadProps) {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead>
      <button
        type="button"
        className={cn(
          "inline-flex items-center gap-1 -mx-2 px-2 py-1 rounded",
          "hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
          active ? "text-text" : "text-text-muted",
        )}
        onClick={() =>
          onChange(
            active
              ? { key: sortKey, dir: sort.dir === "asc" ? "desc" : "asc" }
              : { key: sortKey, dir: "desc" },
          )
        }
        aria-sort={
          active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
        }
      >
        <span>{label}</span>
        <Icon className="h-3 w-3" aria-hidden />
      </button>
    </TableHead>
  );
}

/**
 * Pure sort over the response array. Server caps to top 20 by cost so
 * we re-sort client-side without a refetch when the user clicks a
 * different column. Nullable timestamps sink to the end on either
 * direction so a still-running job doesn't elbow expensive completed
 * jobs off the top of the list.
 *
 * Exported for unit tests — the public surface of this module is the
 * default {@link CostDashboard} component, but the sort logic is the
 * load-bearing piece worth testing directly without RTL plumbing.
 */
export function sortRuns(
  rows: readonly UsageByJob[],
  { key, dir }: RunsSortState,
): UsageByJob[] {
  const factor = dir === "asc" ? 1 : -1;
  const compareNullable = (a: number | null, b: number | null): number => {
    if (a === null && b === null) return 0;
    if (a === null) return 1; // nulls always last
    if (b === null) return -1;
    return (a - b) * factor;
  };
  return [...rows].sort((a, b) => {
    switch (key) {
      case "cost":
        return (Number(a.costUsd) - Number(b.costUsd)) * factor;
      case "calls":
        return (a.callCount - b.callCount) * factor;
      case "avgLatency":
        return (a.avgLatencyMs - b.avgLatencyMs) * factor;
      case "started":
        return compareNullable(parseTime(a.startedAt), parseTime(b.startedAt));
      case "duration":
        return compareNullable(durationMs(a), durationMs(b));
    }
  });
}

function parseTime(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

function durationMs(row: UsageByJob): number | null {
  return diffMsFromIso(row.startedAt, row.completedAt);
}

function formatDuration(row: UsageByJob): string {
  return formatDurationMs(durationMs(row));
}
